// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { otAssert, toRoundedAmount } from '@shared/conversions'
import { RoleEnum } from '@shared/Enums'
import { PlanActionEnum } from '@shared/PlanActionCTO'
import { DynamicWithdrawalActivityCTO } from '@shared/PoolEventCTO'
import { EthAddr } from '@shared/SchemaTypes'
import { ethers } from 'ethers'
import { LiquidityAssetClass } from './LiquidityAssetClass'
import { LytReservesClass } from './LytReservesClass'
import { ReserveActionPlanClass, ReservePlanModeEnum } from './ReserveActionPlanClass'

export interface PlanActionClassTestOutput {
  poolName: string
  actionEnum: PlanActionEnum
  defaultAssets: string
  actualAssets: string
  postAssetBalance: string
  postPercentReserves: number
  isLiquidityAsset: boolean
  completed: boolean
  redeemUuid?: string
  marketMakerAddr?: EthAddr
}
export class PlanActionClass {
  constructor(
    action:
      | { reserveId: string; reserveActionPlanClass: ReserveActionPlanClass }
      | { transferAmount: bigint; reserveActionPlanClass: ReserveActionPlanClass }
      | { dynamicWithdrawalActivityCTO: DynamicWithdrawalActivityCTO; reserveActionPlanClass: ReserveActionPlanClass }
  ) {
    if ('reserveId' in action) {
      const { reserveId, reserveActionPlanClass } = action

      const reserve = reserveActionPlanClass.reservesWithStats.find((r) => r.id === reserveId)
      otAssert(reserve, 'reserve not found')
      this.reserveActionPlanClass = reserveActionPlanClass
      this.id = ethers.uuidV4(ethers.randomBytes(16))
      this.reserveId = reserve.id

      this.defaultShares = 0n
      this.actualShares = 0n
      this.defaultFees = 0n
      this.actualFees = 0n
      this.redeemUuid = undefined
      this.marketMakerAddr = undefined

      otAssert(reserve, 'reserve not found')

      if (action.reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.InflowMode) {
        this.reserveActionPlanClass = reserveActionPlanClass

        otAssert(reserve, 'reserve not found')

        this.defaultAssets = reserve.defaultDepositAmount

        if (reserve.isLiquidityAsset) {
          this._completed = true
          this.actionEnum = PlanActionEnum.ReserveBufferRetainDeposit
          this.roleEnum = RoleEnum.NONE
        } else {
          this._completed = false
          this.actionEnum = PlanActionEnum.ReserveNativeDeposit
          this.roleEnum = RoleEnum.ISSUER_VAULT_WALLET
        }
        if (this.actualAssets === 0n) {
          this._completed = true
        }
      } else if (action.reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.RetrieveMode) {
        this.actionEnum = PlanActionEnum.UNDEFINED
        this.defaultAssets = reserve.defaultWithdrawAmount

        if (reserve.isLiquidityAsset) {
          this._completed = true
          this.actionEnum = PlanActionEnum.ReserveRedeemBufferRetain
          this.roleEnum = RoleEnum.NONE
        } else {
          this._completed = false
          this.actionEnum = PlanActionEnum.RequestReserveRedeem
          this.roleEnum = RoleEnum.ISSUER_VAULT_WALLET
        }
        if (this.actualAssets === 0n) {
          this._completed = true
        }
      } else {
        throw new Error('Invalid reserve plan mode')
      }
    } else if ('transferAmount' in action) {
      this.reserveActionPlanClass = action.reserveActionPlanClass
      this.id = ethers.uuidV4(ethers.randomBytes(16))
      this.reserveId = 'NA'
      this.actionEnum = PlanActionEnum.TransferNativeForMarketMakerRedeem
      this.roleEnum = RoleEnum.ISSUER_VAULT_WALLET
      this.defaultAssets = action.transferAmount
      this._completed = false
      this.defaultShares = 0n
      this.defaultFees = 0n
      this.actualShares = 0n
      this.actualFees = 0n
      this.redeemUuid = undefined
      this.marketMakerAddr = undefined
    } else if ('dynamicWithdrawalActivityCTO' in action) {
      otAssert(action.dynamicWithdrawalActivityCTO.uuid, 'uuid is required')
      otAssert(action.dynamicWithdrawalActivityCTO.lender, 'lender is required')
      this.reserveActionPlanClass = action.reserveActionPlanClass
      this.id = ethers.uuidV4(ethers.randomBytes(16))
      this.reserveId = 'NA'
      this.actionEnum = PlanActionEnum.ProcessRedemptionForMarketMaker
      this.roleEnum = RoleEnum.ISSUER
      this.defaultAssets = BigInt(action.dynamicWithdrawalActivityCTO.assets)
      this._completed = false
      this.defaultShares = BigInt(action.dynamicWithdrawalActivityCTO.shares)
      this.redeemUuid = action.dynamicWithdrawalActivityCTO.uuid
      this.marketMakerAddr = action.dynamicWithdrawalActivityCTO.lender
      this.defaultFees = 0n
      this.actualShares = 0n
      this.actualFees = 0n
    } else {
      throw new Error('Invalid action')
    }
  }

  reserveActionPlanClass: ReserveActionPlanClass
  id: string
  roleEnum: RoleEnum
  actionEnum: PlanActionEnum
  reserveId: string
  defaultAssets: bigint
  defaultShares: bigint
  defaultFees: bigint

  actualShares: bigint
  actualFees: bigint

  redeemUuid?: string
  marketMakerAddr?: EthAddr
  private _completed: boolean

  get completed(): boolean {
    if (
      this.actionEnum === PlanActionEnum.TransferNativeForMarketMakerRedeem &&
      this.reserveActionPlanClass.nativeLiquidityAsset.lytPoolBalance >= this.actualAssets
    ) {
      return true
    }
    if (
      this.reserve &&
      (this.reserve.actualDepositAmount - this.reserve.defaultDepositAmount > 100n ||
        this.reserve.defaultDepositAmount - this.reserve.actualDepositAmount > 100n)
    ) {
      return false
    }
    return this._completed
  }
  set completed(completed: boolean) {
    this._completed = completed
    this.reserveActionPlanClass.notify()
  }

  get reserve(): LytReservesClass | undefined {
    const _reserve = this.reserveActionPlanClass.reservesWithStats.find((reserve) => reserve.id === this.reserveId)
    return _reserve
  }
  get lytPoolAddr(): EthAddr {
    otAssert(
      this.reserveActionPlanClass.lytStageConfiguration.lytPoolAddr,
      'this.reserveActionPlanClass.lytStageConfiguration.lytPoolAddr not found'
    )
    return this.reserveActionPlanClass.lytStageConfiguration.lytPoolAddr
  }
  get poolName(): string {
    return this.reserve?.poolName ?? 'NA'
  }

  get nativeLiquidityAsset(): LiquidityAssetClass {
    otAssert(
      this.reserveActionPlanClass.nativeLiquidityAsset,
      'this.reserveActionPlanClass.nativeLiquidityAsset not found'
    )
    return this.reserveActionPlanClass.nativeLiquidityAsset
  }
  onlyPositive(value: bigint): bigint {
    return value > 0n ? value : 0n
  }
  get actualAssets(): bigint {
    if (this.actionEnum === PlanActionEnum.TransferNativeForMarketMakerRedeem) {
      return this.onlyPositive(this.defaultAssets)
    }
    if (this.actionEnum === PlanActionEnum.ProcessRedemptionForMarketMaker) {
      return this.onlyPositive(this.defaultAssets)
    }
    otAssert(this.reserve, 'reserve not found')
    if (
      this.actionEnum === PlanActionEnum.RequestReserveRedeem ||
      this.actionEnum === PlanActionEnum.ReserveRedeemBufferRetain
    ) {
      return this.onlyPositive(this.reserve.actualWithdrawAmount)
    }
    return this.onlyPositive(this.reserve.actualDepositAmount)
  }

  get testOutput(): PlanActionClassTestOutput {
    return {
      poolName: this.poolName,
      actionEnum: this.actionEnum,
      defaultAssets: toRoundedAmount(this.defaultAssets),
      actualAssets: toRoundedAmount(this.actualAssets),
      postAssetBalance: toRoundedAmount(this.reserve?.postAssetBalance ?? 0n),
      postPercentReserves: Math.round((this.reserve?.postPercentReserves ?? 0) * 100) / 100,
      isLiquidityAsset: this.reserve?.isLiquidityAsset ?? false,
      completed: this.completed,
      redeemUuid: this.redeemUuid,
      marketMakerAddr: this.marketMakerAddr,
    }
  }
  toString(): string {
    const structuredOutput = this.testOutput
    return JSON.stringify(structuredOutput, null, 2)
  }
  [Symbol.for('nodejs.util.inspect.custom')](): string {
    return this.toString()
  }
  validate() {}
}
